import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { useSignalRNegotiate } from '../../api/NotificationApi';
import { useUserStore } from '../../app/store/useUserStore';
import { notificationStorage } from '../utils/notificationStorage';
import { store } from '../../app/store';
import { addNotification } from '../../app/store/slices/notificationSlice';
import type { SignalRNotification, Notification } from '../../types/notification';
import { NotificationType } from '../../types/notification';
import { getLogger } from '../utils/logger';
import { getNotificationTitle, getNotificationMessage } from '../utils/notificationMessages';
import i18n from '../i18n/config';
import { getAuth0Token } from '../../auth/utils';
import { useToastStore } from '../../app/store/useToastStore';
import { performFullLogout } from '../utils/logoutCleanup';

const logger = getLogger('SignalRContext');

interface SignalRContextType {
  connection: signalR.HubConnection | null;
  isConnected: boolean;
  connectionId: string | null;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

const SignalRContext = createContext<SignalRContextType | undefined>(undefined);

// Singleton connection instance
let globalConnection: signalR.HubConnection | null = null;
let isConnecting = false;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 4;
const BASE_RECONNECT_DELAY = 1000;
let isIntentionalDisconnect = false;
let isWaitingForTokenRefresh = false;
let isNegotiating = false; // true while negotiate HTTP call is in-flight; prevents onclose double-retry

// Deduplication tracker
const recentNotifications = new Map<string, number>();
const DEDUPLICATION_WINDOW = 3000; // 3 seconds

export const SignalRProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const { user } = useUserStore();
  const { mutateAsync: negotiate } = useSignalRNegotiate();
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Generate deduplication key
  const getNotificationKey = (notification: SignalRNotification): string => {
    // Use notification_id when available — it's a unique backend ID, best dedup key
    if (notification.data?.notification_id) {
      return `${notification.type}-${notification.data.notification_id}`;
    }
    // For chat messages, include message_id to ensure each message is unique
    // (group chat title/message are fixed translations, so the fallback key would collide)
    if ((notification.type === 'chat_message' || notification.type === 'order_group_chat') && notification.data?.message_id) {
      return `${notification.type}-${notification.data.message_id}`;
    }
    return `${notification.type}-${notification.title}-${notification.message}`;
  };

  // Check if notification is duplicate
  const isDuplicate = (notification: SignalRNotification): boolean => {
    const key = getNotificationKey(notification);
    const lastTime = recentNotifications.get(key);
    const now = Date.now();

    if (lastTime && now - lastTime < DEDUPLICATION_WINDOW) {
      logger.warn('Duplicate notification suppressed:', {
        key,
        msSinceLast: now - lastTime,
        notification_id: notification.data?.notification_id,
        type: notification.type,
        status: notification.data?.status,
      });
      return true;
    }

    recentNotifications.set(key, now);

    // Clean up old entries
    setTimeout(() => {
      recentNotifications.delete(key);
    }, DEDUPLICATION_WINDOW);

    return false;
  };

  // Enrich notification data
  const enrichNotification = (signalRNotif: SignalRNotification): Omit<Notification, 'id' | 'timestamp'> => {
    // Format title for chat messages
    let displayTitle = signalRNotif.title;
    if (signalRNotif.type === 'chat_message') {
      displayTitle = 'A New Message Received';
    }

    const currentUser = useUserStore.getState().user;
    const currentUserRole = currentUser?.user_role as 'traveller' | 'sender' | undefined;

    const rawName = signalRNotif.message
      ?.trim()
      ?.split(/\s+/)
      ?.slice(0, 2)
      ?.join(' ') || '';
    const localUserId = currentUser?.uuid || String(currentUser?.user_id || '') || signalRNotif.user_id;

    const notifUserRole = currentUserRole;

    const baseNotification: Omit<Notification, 'id' | 'timestamp'> = {
      title: displayTitle,
      message: signalRNotif.message,
      type: signalRNotif.type,
      isRead: false,
      userId: localUserId,
      user_role: notifUserRole,
      data: {
        ...signalRNotif.data,
        statusFlag: 'pending',
        isRealTime: true,
        ...(rawName && { displayName: rawName, name: rawName }),
        ...((signalRNotif as any).chat_room_id && { chat_room_id: String((signalRNotif as any).chat_room_id) }),
        ...((signalRNotif as any).chat_roomId != null && { chat_roomId: (signalRNotif as any).chat_roomId }),
        ...((signalRNotif as any).order_code && { order_code: (signalRNotif as any).order_code }),
        ...((signalRNotif as any).sender && { sender: (signalRNotif as any).sender }),
      },
    };

    // Override title/message with translation-based text for offer notifications
    if (signalRNotif.type !== 'chat_message' && signalRNotif.type !== 'order_group_chat' && notifUserRole) {
      const translatedTitle = getNotificationTitle(baseNotification as Notification, notifUserRole);
      const translatedMessage = getNotificationMessage(baseNotification as Notification, notifUserRole);
      baseNotification.title = translatedTitle;
      baseNotification.message = translatedMessage;
    }

    // Add specific data based on type
    if (signalRNotif.data?.shipment_id) {
      baseNotification.relatedEntityId = `shipment_${signalRNotif.data.shipment_id}`;
    } else if (signalRNotif.data?.itinerary_id) {
      baseNotification.relatedEntityId = `itinerary_${signalRNotif.data.itinerary_id}`;
    } else if (signalRNotif.data?.chat_room_id || (signalRNotif as any).chat_room_id) {
      baseNotification.relatedEntityId = `chat_${signalRNotif.data?.chat_room_id ?? (signalRNotif as any).chat_room_id}`;
    }

    return baseNotification;
  };

  // Dispatches a notification to Redux (which is the single writer to notificationStorage)
  // and fires the follow-up events UI listens for. Returns the persisted notification, or
  // null if it was rejected as a duplicate by notificationStorage.
  const dispatchNotification = (
    notification: Omit<Notification, 'id' | 'timestamp'>,
    onExtra?: (saved: Notification) => void
  ): Notification | null => {
    const before = notificationStorage.getNotifications();
    const beforeTopId = before[0]?.id;
    store.dispatch(addNotification(notification));
    const notifications = notificationStorage.getNotifications();

    if (notifications.length === before.length && notifications[0]?.id === beforeTopId) {
      // notificationStorage rejected it as an exact duplicate — nothing changed
      return null;
    }

    // A new notification is unshifted to the front; an updated one (e.g. a chat
    // message count bump) is also moved to the front — either way this is it.
    const savedNotification = notifications[0];
    window.dispatchEvent(new CustomEvent('signalr-notification', { detail: savedNotification }));
    window.dispatchEvent(new CustomEvent('notification-state-changed'));
    onExtra?.(savedNotification);
    return savedNotification;
  };

  // Handle KYC verification notification
  const handleKycNotification = (signalRNotif: SignalRNotification) => {
    const kycStatus = signalRNotif.data?.status;

    if (!kycStatus) {
      logger.warn('KYC notification received without status');
      return;
    }

    // Update user's KYC status in the store
    const { updateUserProfile } = useUserStore.getState();
    updateUserProfile({ kyc_status: String(kycStatus).toLowerCase() });

    const cardVerified = useUserStore.getState().user?.card_verified;

    logger.info('Handling KYC notification:', {
      notification_id: signalRNotif.data?.notification_id,
      status: kycStatus,
      cardVerified,
      user_id: signalRNotif.user_id,
      raw: signalRNotif,
    });

    // Map KYC status to translation keys and KYC-specific toast types
    const kycConfigMap: Record<string, { translationKey: string; toastType: string }> = {
      'verified': cardVerified
        ? {
          translationKey: 'messages.kycVerified',
          toastType: 'kycVerified'
        }
        : {
          translationKey: 'messages.kycCardPending',
          toastType: 'kycCardPending'
        },
      'requires_input': {
        translationKey: 'messages.kycRequiresInput',
        toastType: 'kycRequiresInput'
      },
      'canceled': {
        translationKey: 'messages.kycCanceled',
        toastType: 'kycCanceled'
      },
      'processing': {
        translationKey: 'messages.kycProcessing',
        toastType: 'kycProcessing'
      },
      'restricted': {
        translationKey: 'messages.kycRestricted',
        toastType: 'kycRestricted'
      }
    };

    const kycConfig = kycConfigMap[String(kycStatus)];

    if (kycConfig) {
      const message = i18n.t(kycConfig.translationKey);
      // Dispatch a custom event to trigger the toast
      window.dispatchEvent(new CustomEvent('kyc-status-changed', {
        detail: {
          status: kycStatus,
          message,
          type: kycConfig.toastType
        }
      }));

      // Also save to notification storage and Redux so it appears in the bell icon dropdown
      const kycUserRole = useUserStore.getState().user?.user_role as 'traveller' | 'sender' | undefined;
      const kycNotification: Omit<Notification, 'id' | 'timestamp'> = {
        title: i18n.t('messages.kycVerification'),
        message,
        type: 'kyc_verification',
        isRead: false,
        userId: signalRNotif.user_id,
        user_role: kycUserRole,
        data: {
          ...signalRNotif.data,
          kycStatus: kycStatus,
          toastType: kycConfig.toastType,
          isRealTime: true,
        },
      };

      dispatchNotification(kycNotification);
    }

    logger.info('KYC status updated:', kycStatus);
  };

  const handlePayoneerNotification = (signalRNotif: SignalRNotification) => {
    const payoneerStatus = signalRNotif.data?.status as string | undefined;

    if (!payoneerStatus) return;

    const payoneerConfigMap: Record<string, { translationKey: string; toastType: string }> = {
      pending: { translationKey: 'messages.payoneerPending', toastType: 'payoutPending' },
      active: { translationKey: 'messages.payoneerActive', toastType: 'payoutActive' },
      declined: { translationKey: 'messages.payoneerDeclined', toastType: 'payoutDeclined' },
      rekyc_required: { translationKey: 'messages.payoneerRekycRequired', toastType: 'payoutDeclined' },
      kyc_required: { translationKey: 'messages.payoneerKycRequired', toastType: 'payoutPending' },
      manual_review: { translationKey: 'messages.payoneerManualReview', toastType: 'payoutManualReview' },
      bank_pending: { translationKey: 'messages.payoneerBankPending', toastType: 'payoutBankPending' },
    };

    const config = payoneerConfigMap[payoneerStatus];
    if (!config) return;

    const message = i18n.t(config.translationKey);

    window.dispatchEvent(new CustomEvent('kyc-status-changed', {
      detail: { status: payoneerStatus, message, type: config.toastType },
    }));

    const currentUserRole = useUserStore.getState().user?.user_role as 'traveller' | 'sender' | undefined;
    const payoneerNotification: Omit<Notification, 'id' | 'timestamp'> = {
      title: i18n.t('messages.payoneerOnboarding'),
      message,
      type: 'payoneer_onboarding',
      isRead: false,
      userId: signalRNotif.user_id,
      user_role: currentUserRole,
      data: {
        ...signalRNotif.data,
        payoneerStatus,
        toastType: config.toastType,
        isRealTime: true,
      },
    };

    dispatchNotification(payoneerNotification);
  };

  const handlePayoutRequestNotification = (signalRNotif: SignalRNotification) => {
    const payoutStatus = signalRNotif.data?.status as string | undefined;

    if (!payoutStatus) return;

    const payoutConfigMap: Record<string, { translationKey: string; toastType: string }> = {
      pending: { translationKey: 'messages.payoutRequestPending', toastType: 'payoutPending' },
      under_review: { translationKey: 'messages.payoutRequestUnderReview', toastType: 'payoutPending' },
      processing: { translationKey: 'messages.payoutRequestProcessing', toastType: 'payoutPending' },
      completed: { translationKey: 'messages.payoutRequestCompleted', toastType: 'payoutActive' },
      approved: { translationKey: 'messages.payoutRequestCompleted', toastType: 'payoutActive' },
      failed: { translationKey: 'messages.payoutRequestFailed', toastType: 'payoutDeclined' },
      cancelled: { translationKey: 'messages.payoutRequestCancelled', toastType: 'payoutDeclined' },
      rejected: { translationKey: 'messages.payoutRequestRejected', toastType: 'payoutDeclined' },
      refunded: { translationKey: 'messages.payoutRequestRefunded', toastType: 'payoutManualReview' },
    };

    const config = payoutConfigMap[payoutStatus];
    if (!config) return;

    const message = i18n.t(config.translationKey);

    window.dispatchEvent(new CustomEvent('kyc-status-changed', {
      detail: { status: payoutStatus, message, type: config.toastType },
    }));

    const currentUserRole = useUserStore.getState().user?.user_role as 'traveller' | 'sender' | undefined;
    const payoutNotification: Omit<Notification, 'id' | 'timestamp'> = {
      title: i18n.t('messages.payoutRequest'),
      message,
      type: 'payout_request',
      isRead: false,
      userId: signalRNotif.user_id,
      user_role: currentUserRole,
      data: {
        ...signalRNotif.data,
        payoutStatus,
        toastType: config.toastType,
        isRealTime: true,
      },
    };

    dispatchNotification(payoutNotification);
  };

  const handleIncomingNotification = (signalRNotif: SignalRNotification) => {
    // Log all incoming notifications with full details
    logger.info('Received SignalR notification:', {
      fullNotification: signalRNotif
    });

    // Check if this is a chat message notification
      if ((signalRNotif as any).chatMessage || signalRNotif.data?.chatMessage) {
      handleIncomingMessage(signalRNotif);
      return;
    }

    if (signalRNotif.type === NotificationType.PASSWORD_CHANGED || signalRNotif.type === NotificationType.ACCOUNT_DELETED) {
      if (signalRNotif.type === NotificationType.ACCOUNT_DELETED) {
        useToastStore.getState().showToast(signalRNotif.message || signalRNotif.title || '', 'info', 10000);
      }
      setTimeout(() => {
        performFullLogout();
      }, 10000);
      return;
    }

    if (signalRNotif.type === 'campaign') {
      if (!isDuplicate(signalRNotif)) {
        useToastStore.getState().showToast(signalRNotif.message || signalRNotif.title || '', 'info');
      }
    }

    if (signalRNotif.type === 'kyc_verification') {
      if (!isDuplicate(signalRNotif)) {
        handleKycNotification(signalRNotif);
      }
      return;
    }

    if (signalRNotif.type === 'payoneer_onboarding') {
      if (!isDuplicate(signalRNotif)) {
        handlePayoneerNotification(signalRNotif);
      }
      return;
    }

    if (signalRNotif.type === 'payout_request') {
      if (!isDuplicate(signalRNotif)) {
        handlePayoutRequestNotification(signalRNotif);
      }
      return;
    }

    // Check for duplicates
    if (isDuplicate(signalRNotif)) {
      return;
    }


    // Don't show chat_room_created notification to the person who created the room
    if (signalRNotif.type === 'chat_room_created') {
      const currentUser = useUserStore.getState().user;
      // sender.id is a UUID — compare against uuid, not the numeric user_id
      const currentUserUuid = currentUser?.uuid?.toString();
      const senderId = ((signalRNotif as any).sender?.id)?.toString();
      if (currentUserUuid && senderId && currentUserUuid === senderId) {
        return;
      }
    }

    // Enrich the notification
    const enrichedNotif = enrichNotification(signalRNotif);
    const savedNotification = dispatchNotification(enrichedNotif, () => {
      // When a sender receives an offer request, trigger item listing refresh
      if (signalRNotif.type === 'shipment_offer_request' && enrichedNotif.user_role === 'sender') {
        window.dispatchEvent(new CustomEvent('shipment-offer-received'));
      }
    });

    if (!savedNotification) {
      logger.warn('Failed to save notification to localStorage');
    }
  };

  // Handle incoming message
  const handleIncomingMessage = (messageData: unknown) => {
    interface ChatMsg {
      sender_id?: string | number;
      sender?: { id?: string | number; full_name?: string; profile_image?: string };
      receiver_id?: string;
      message?: string;
      chat_room_id?: string;
      order_id?: string | number;
      order_code?: string;
      message_type?: string;
      id?: string | number;
    }
    const md = messageData as Record<string, unknown> | undefined;
    const chatMsg = (
      (md?.fullNotification as Record<string, unknown> | undefined)?.chatMessage ||
      (md?.data as Record<string, unknown> | undefined)?.chatMessage ||
      md?.chatMessage ||
      messageData
    ) as ChatMsg | undefined;

    const liveUser = useUserStore.getState().user;
    const currentUserId = liveUser?.user_id?.toString();
    const senderId = (chatMsg?.sender_id ?? chatMsg?.sender?.id)?.toString();

    const isReceiver = currentUserId && senderId && currentUserId !== senderId;

    window.dispatchEvent(new CustomEvent('signalr-chat-message', {
      detail: messageData,
    }));

    if (isReceiver && chatMsg?.sender?.full_name && chatMsg?.message && chatMsg?.chat_room_id) {
      const signalRNotif: SignalRNotification = {
        user_id: chatMsg.receiver_id || liveUser?.uuid || '',
        type: 'chat_message',
        title: 'New message', // Will be formatted by enrichNotification
        message: chatMsg.message,
        data: {
          sender_name: chatMsg.sender.full_name,
          sender_id: chatMsg.sender_id ?? chatMsg.sender.id,
          sender_profile_image: chatMsg.sender.profile_image,
          chat_room_id: chatMsg.chat_room_id,
          order_id: chatMsg.order_id,
          order_code: chatMsg.order_code,
          message_type: chatMsg.message_type,
          message_id: chatMsg.id,
        },
      };

      // Check for duplicates (reuse existing logic)
      if (!isDuplicate(signalRNotif)) {
        const enrichedNotif = enrichNotification(signalRNotif);
        dispatchNotification(enrichedNotif);
      }
    }

  };

  // Handle incoming three-way (sender/traveler/receiver) order group chat message
  const handleIncomingGroupChatMessage = (messageData: unknown) => {
    interface GroupChatMsg {
      id?: string;
      chat_room_id?: string;
      message?: string;
      message_type?: string;
      sender?: { role?: string; id?: string | null; full_name?: string; profile_image?: string | null };
      order_code?: string;
    }
    const md = messageData as Record<string, unknown> | undefined;
    const chatMsg = (md?.chatMessage || messageData) as GroupChatMsg | undefined;
    if (!chatMsg) return;

    const liveUser = useUserStore.getState().user;
    const currentUserUuid = liveUser?.uuid?.toString();
    const senderId = chatMsg.sender?.id?.toString();

    // Skip notifying the person who sent the message
    if (currentUserUuid && senderId && currentUserUuid === senderId) return;

    if (!chatMsg.sender?.full_name || !chatMsg.message || !chatMsg.chat_room_id) return;

    const title = i18n.t('notification.orderGroupChat.title');
    const message = i18n.t('notification.orderGroupChat.message');

    const signalRNotif: SignalRNotification = {
      user_id: liveUser?.uuid || '',
      type: 'order_group_chat',
      title,
      message,
      data: {
        sender_name: chatMsg.sender.full_name,
        sender_role: chatMsg.sender.role,
        sender_id: chatMsg.sender.id ?? undefined,
        sender_profile_image: chatMsg.sender.profile_image ?? undefined,
        chat_room_id: chatMsg.chat_room_id,
        order_code: chatMsg.order_code,
        message_type: chatMsg.message_type,
        message_id: chatMsg.id,
      },
    };

    if (!isDuplicate(signalRNotif)) {
      const enrichedNotif = enrichNotification(signalRNotif);
      dispatchNotification(enrichedNotif);
    }
  };

  // Setup event listeners
  const setupEventListeners = (connection: signalR.HubConnection) => {
    // Main notification listener
    connection.on('ReceiveNotification', (data: unknown) => {
      const d = data as Record<string, unknown> | undefined;
      const notification = (d?.notificationReceived || d?.fullNotification || data) as SignalRNotification;
      handleIncomingNotification(notification);
    });

    // Message listener
    connection.on('ReceiveMessage', (messageData: unknown) => {
      handleIncomingMessage(messageData);
    });

    // Order group chat (sender/traveler/receiver three-way chat) message listener
    connection.on('ReceiveGroupChatMessage', (messageData: unknown) => {
      window.dispatchEvent(new CustomEvent('signalr-group-chat-message', {
        detail: messageData,
      }));
      handleIncomingGroupChatMessage(messageData);
    });

    // Edit cart request listener for travelers
    connection.on('edit_cart_request', (data: unknown) => {
      // Convert to notification format
      const d = data as Record<string, unknown> | undefined;
      const signalRNotif: SignalRNotification = {
        user_id: (d?.traveller_id as string | undefined) || user?.uuid || '',
        type: 'edit_cart_request',
        title: (d?.title as string | undefined) || 'Edit Request Received',
        message: (d?.message as string | undefined) || 'A sender has requested to edit an item in the cart',
        data: {
          ...(d || {}),
          type: 'edit_cart_request',
        },
      };

      handleIncomingNotification(signalRNotif);
    });

  };

  // Exponential backoff for reconnection
  const calculateReconnectDelay = (): number => {
    const delay = Math.min(
      BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts),
      30000 // Max 30 seconds
    );
    return delay;
  };

  // Attempt reconnection
  const attemptReconnect = async () => {
    if (isWaitingForTokenRefresh) {
      return;
    }

    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      useToastStore.getState().showToast('Unable to connect to notifications. Please refresh the page.', 'error', 7000);
      return;
    }

    const delay = calculateReconnectDelay();

    reconnectTimeoutRef.current = setTimeout(async () => {
      reconnectAttempts++;
      await connect();
    }, delay);
  };

  // Connect function
  const connect = async () => {
    isIntentionalDisconnect = false;
    // Prevent multiple simultaneous connection attempts
    if (isConnecting) {
      return;
    }

    // Skip if already connected — no need to tear down a healthy connection
    if (
      globalConnection?.state === signalR.HubConnectionState.Connected ||
      globalConnection?.state === signalR.HubConnectionState.Connecting ||
      globalConnection?.state === signalR.HubConnectionState.Reconnecting
    ) {
      return;
    }

    if (!user?.negotiateID && !user?.uuid && !user?.user_id) {
      return;
    }

    const token = getAuth0Token();
    if (!token) {
      return;
    }

    try {
      isConnecting = true;
      isNegotiating = true;

      if (globalConnection) {
        try {
          await globalConnection.stop();
        } catch { /* ignore */ }
        globalConnection = null;
      }

      const negotiateResult = await negotiate({
        user_id: Number(user.negotiateID ?? user.user_id),
      });
      isNegotiating = false;

      // Create new connection
      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(negotiateResult.url, {
          accessTokenFactory: () => negotiateResult.accessToken || '',
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Setup event listeners
      setupEventListeners(newConnection);

      // Connection lifecycle handlers
      newConnection.onclose((_error) => {
        setIsConnected(false);
        setConnectionId(null);
        globalConnection = null;
        // Don't reconnect if disconnect was intentional
        if (isIntentionalDisconnect) {
          isIntentionalDisconnect = false;
          return;
        }
        // Don't reconnect if negotiate itself failed — the catch block handles that
        if (isNegotiating) {
          return;
        }
        // Always attempt reconnection — clean closes (server-side idle timeout) carry no error
        attemptReconnect();
      });

      // Start connection
      await newConnection.start();

      // Update state
      globalConnection = newConnection;
      setIsConnected(true);
      setConnectionId(newConnection.connectionId || null);
      reconnectAttempts = 0;

    } catch (error: unknown) {
      globalConnection = null;
      setIsConnected(false);
      setConnectionId(null);

      // Detect 401 — token expired or invalid.
      // The axios interceptor already dispatches 'token:refresh-needed' on 401,
      // so we just skip reconnect here and wait for 'token:refreshed' to fire.
      const errMsg = String((error as Error)?.message ?? '');
      const status = (error as { response?: { status?: number } })?.response?.status;
      const is401 = status === 401 || errMsg.includes('401') || errMsg.toLowerCase().includes('unauthorized');

      if (is401) {
        logger.warn('Negotiate failed with 401 — token expired. Waiting for token refresh before reconnect...');
        isWaitingForTokenRefresh = true;
        // Do NOT dispatch token:refresh-needed — the axios interceptor already does this.
        // Do NOT attempt reconnect — wait for 'token:refreshed' event from AuthInitializer.
        return;
      } else {
        attemptReconnect();
      }
    } finally {
      isConnecting = false;
      isNegotiating = false;
    }
  };

  // Disconnect function
  const disconnect = async () => {
    isIntentionalDisconnect = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (globalConnection) {
      try {
        await globalConnection.stop();
      } catch { /* ignore */ }
      globalConnection = null;
      setIsConnected(false);
      setConnectionId(null);
    }
  };

  // Auto-connect when user is available
  useEffect(() => {
    if (user?.uuid || user?.user_id) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      // Tear down the connection on unmount/remount (e.g. StrictMode's dev double-invoke)
      // so it isn't orphaned to trigger onclose -> attemptReconnect -> another negotiate.
      disconnect();
    };
  }, [user?.uuid, user?.user_id]);

  // Release the connection slot immediately on tab close instead of waiting for the
  // service's idle timeout — closing a WebSocket doesn't tell Azure SignalR the slot
  // is free until it times the connection out server-side.
  useEffect(() => {
    const handlePageHide = () => {
      isIntentionalDisconnect = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      // Don't await — the page is unloading, there's no guarantee a promise resolves.
      // SignalR sends the close frame over the still-open socket synchronously enough.
      globalConnection?.stop().catch(() => { /* ignore */ });
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
    };
  }, []);

  // Reconnect when the tab becomes visible again after being idle/hidden
  useEffect(() => {
    if (!user?.uuid && !user?.user_id) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const state = globalConnection?.state;
        if (!state || state === signalR.HubConnectionState.Disconnected) {
          // Only reset counter if we're not already mid-retry (reconnectTimeoutRef means a retry is pending)
          if (!reconnectTimeoutRef.current) {
            reconnectAttempts = 0;
          }
          connect();
        }
      }
    };

    const handleFocus = () => {
      const state = globalConnection?.state;
      if (!state || state === signalR.HubConnectionState.Disconnected) {
        if (!reconnectTimeoutRef.current) {
          reconnectAttempts = 0;
        }
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.uuid, user?.user_id]);

  // Reconnect after token refresh completes
  useEffect(() => {
    const handleTokenRefreshed = () => {
      logger.info('Token refreshed, attempting SignalR reconnect...');
      isWaitingForTokenRefresh = false;
      reconnectAttempts = 0;
      connect();
    };

    window.addEventListener('token:refreshed', handleTokenRefreshed);

    return () => {
      window.removeEventListener('token:refreshed', handleTokenRefreshed);
    };
  }, []);

  const value: SignalRContextType = {
    connection: globalConnection,
    isConnected,
    connectionId,
    connect,
    disconnect,
  };

  return (
    <SignalRContext.Provider value={value}>
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalR = (): SignalRContextType => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error('useSignalR must be used within SignalRProvider');
  }
  return context;
};
